import { Cart } from "@insite/client-framework/Services/CartService";
import {
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { OrderModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { PromotionMessagesModel } from "@store/Types/ApiModels";

export interface LinePricingData {
    totalDiscountedPrice: number;
    totalRegularPrice: number;
}

export function getDiscountProductsInCategoryPromotions(promotions: PromotionModel[]) {
    const discounts = promotions.filter(promotion => promotion.promotionResultType === "DiscountProductsInCategory");
    const mergedDiscounts = discounts.reduce((acc: PromotionModel[], curr: PromotionModel) => {
        const existing = acc.find((item: PromotionModel) => item.name === curr.name);
        if (existing) {
            existing.amount += curr.amount;
            const totalAmount = existing.amount;
            existing.amountDisplay = `$${totalAmount.toFixed(2)}`;
        } else {
            acc.push({ ...curr });
        }
        return acc;
    }, []);
    return mergedDiscounts;
}

export function getPromotionsExcept(
    promotions: PromotionModel[],
    ...exceptionList: PromotionModel[]
): PromotionModel[] {
    const exceptionNames = exceptionList.map(promotion => promotion.name);
    const others = promotions.filter(promotion => !exceptionNames.includes(promotion.name));

    return others.reduce((acc: PromotionModel[], curr: PromotionModel) => {
        const existing = acc.find(item => item.name === curr.name);
        if (existing) {
            existing.amount += curr.amount;
            const totalAmount = existing.amount;
            existing.amountDisplay = `$${totalAmount.toFixed(2)}`;
        } else {
            acc.push({ ...curr });
        }

        return acc;
    }, []);
}

export function getPromotionsTotalAmount(promotions: PromotionModel[]): number {
    return promotions.reduce((acc: number, curr: PromotionModel) => acc + curr.amount, 0);
}

export function getDiscountTotal(promotions: PromotionModel[]) {
    return getOrderPromotions(promotions)
        .concat(getShippingPromotions(promotions))
        .concat(getDiscountProductsInCategoryPromotions(promotions))
        .map(promotion => promotion.amount)
        .reduce((prev, curr) => curr + prev, 0);
}

export function getCartLinesPricings(cart?: Cart): LinePricingData[] {
    return (
        cart?.cartLines?.map(cl => ({
            totalRegularPrice: cl.pricing && cl.qtyOrdered ? cl.pricing.regularPrice * cl.qtyOrdered : 0,
            totalDiscountedPrice: cl.pricing ? cl.pricing.unitRegularPrice * cl.qtyOrdered! : 0,
        })) ?? []
    );
}

export function getOrderLinesPricing(order?: OrderModel): LinePricingData[] {
    if (!order?.orderLines?.length) {
        return [];
    }
    return order.orderLines.map(ol => ({
        totalRegularPrice: ol.unitListPrice * ol.qtyOrdered,
        totalDiscountedPrice: ol.totalRegularPrice,
    }));
}

export function getPricingSubTotal(linePricings: LinePricingData[]): number {
    if (!linePricings.length) {
        return 0;
    }

    return linePricings.reduce((acc: number, curr: LinePricingData) => acc + curr.totalRegularPrice, 0);
}

export function getPricingDiscountTotal(linePricings: LinePricingData[]): number {
    if (!linePricings.length) {
        return 0;
    }

    return linePricings
        .map(cl => cl.totalRegularPrice - cl.totalDiscountedPrice)
        .reduce((acc: number, curr: number) => acc + curr, 0);
}

export function getPromotionMessages(state: StoreApplicationState): PromotionMessagesModel[] {
    return state.store.data.promotionMessages.promotionMessages;
}
